import axios from '../config/axios';
import { API_ENDPOINTS } from '../config/api';

const BASE = `${(
  process.env.REACT_APP_API_URL ||
  'https://backend-clinical-intake.dizzaroo.com'
).replace(/\/$/, '')}/api/feasibility`;

const feasibilityService = {
  async submit({ projectId, receiverEmail, agreementPdf, feasibilityForm, protocolSynopsis, metadata }) {
    const form = new FormData();
    form.append('projectId', projectId);
    form.append('receiverEmail', receiverEmail);
    if (metadata) form.append('metadata', JSON.stringify(metadata));
    form.append('agreementPdf', agreementPdf);
    form.append('feasibilityForm', feasibilityForm);
    form.append('protocolSynopsis', protocolSynopsis);

    const res = await axios.post(`${BASE}/submit`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      withCredentials: true,
    });
    return res.data;
  },

  async issueLinks(requestId) {
    const res = await axios.post(`${BASE}/${requestId}/issue-links`, {}, { withCredentials: true });
    return res.data;
  },

  getDownloadUrl(token) {
    return `${BASE}/download/${encodeURIComponent(token)}`;
  },

  async getQuestionnaire(projectId) {
    const res = await axios.get(`${BASE}/questionnaire`, {
      params: { projectId },
      withCredentials: true,
    });
    return res.data?.data || null;
  },

  async saveQuestionnaire(projectId, questionnaire) {
    const res = await axios.post(
      `${BASE}/questionnaire`,
      {
        projectId,
        questionnaire,
      },
      { withCredentials: true }
    );
    return res.data?.data || null;
  },

  async getCountryReport(projectId) {
    const res = await axios.get(`${BASE}/country-report`, {
      params: { projectId },
      withCredentials: true,
    });
    return res.data?.data || null;
  },

  async saveCountryReport(projectId, payload) {
    const res = await axios.post(
      `${BASE}/country-report`,
      {
        projectId,
        ...payload,
      },
      { withCredentials: true }
    );
    return res.data?.data || null;
  },

  async getSiteReport(projectId) {
    const res = await axios.get(`${BASE}/site-report`, {
      params: { projectId },
      withCredentials: true,
    });
    return res.data?.data || null;
  },

  async saveSiteReport(projectId, payload) {
    const res = await axios.post(
      `${BASE}/site-report`,
      {
        projectId,
        ...payload,
      },
      { withCredentials: true }
    );
    return res.data?.data || null;
  },

   async generateSiteSummaries(projectId, rows) {
    const res = await axios.post(
      `${BASE}/site-report/generate-summaries`,
      { projectId, rows },
      { withCredentials: true }
    );
    return res.data?.summaries ?? [];
  },

  async getPostgresFeasibilityData(projectId, forceRefresh = false) {
    try {
      if (!projectId) {
        throw new Error('projectId is required to fetch PostgreSQL feasibility data');
      }
      const res = await axios.get(`${BASE}/postgres-data`, {
        params: { 
          projectId,
          ...(forceRefresh ? { forceRefresh: true } : {})
        },
        withCredentials: true,
      });
      // Handle response structure: { success: true, data: [...], count: ..., diagnosisCondition: ... }
      return res.data;
    } catch (error) {
      console.error('Error fetching PostgreSQL data:', error);
      throw error;
    }
  },

    async regenerateMeshTerms(projectId) {
    try {
      if (!projectId) {
        throw new Error('projectId is required to regenerate MeSH terms');
      }
      const res = await axios.post(`${BASE}/regenerate`, null, {
        params: { projectId },
        withCredentials: true,
      });
      return res.data;
    } catch (error) {
      console.error('Error regenerating MeSH terms:', error);
      throw error;
    }
  },


  async processTrialPopulationSummary(projectId) {
    try {
      if (!projectId) {
        throw new Error('projectId is required to process trial population summary');
      }
      const res = await axios.post(`${BASE}/trial-population-summary`, null, {
        params: { projectId },
        withCredentials: true,
      });
      return res.data;
    } catch (error) {
      console.error('Error processing trial population summary:', error);
      throw error;
    }
  },

  async getTrialPopulationSummary(projectId) {
    try {
      if (!projectId) {
        throw new Error('projectId is required to fetch trial population summary');
      }
      const res = await axios.get(`${BASE}/trial-population-summary`, {
        params: { projectId },
        withCredentials: true,
      });
      return res.data;
    } catch (error) {
      console.error('Error fetching trial population summary:', error);
      throw error;
    }
  },

  async updateTrialPopulationSummary(projectId, summary) {
    try {
      if (!projectId) {
        throw new Error('projectId is required to update trial population summary');
      }
      if (!summary || typeof summary !== 'string' || summary.trim().length === 0) {
        throw new Error('summary is required and cannot be empty');
      }
      const res = await axios.put(`${BASE}/trial-population-summary`, {
        projectId,
        summary: summary.trim(),
      }, {
        withCredentials: true,
      });
      return res.data;
    } catch (error) {
      console.error('Error updating trial population summary:', error);
      throw error;
    }
  },

  // Eligibility Embeddings methods
  async generateEligibilityEmbeddings(batchSize = 50, limit = null) {
    try {
      const res = await axios.post(
        `${BASE}/eligibility-embeddings/generate`,
        { batchSize, limit },
        { withCredentials: true }
      );
      return res.data;
    } catch (error) {
      console.error('Error generating eligibility embeddings:', error);
      throw error;
    }
  },

  async getEligibilityEmbeddingStats() {
    try {
      const res = await axios.get(`${BASE}/eligibility-embeddings/stats`, {
        withCredentials: true,
      });
      return res.data;
    } catch (error) {
      console.error('Error fetching eligibility embedding stats:', error);
      throw error;
    }
  },

  async getEligibilityEmbedding(nctId) {
    try {
      const res = await axios.get(`${BASE}/eligibility-embeddings/${nctId}`, {
        withCredentials: true,
      });
      return res.data;
    } catch (error) {
      console.error('Error fetching eligibility embedding:', error);
      throw error;
    }
  },

  async searchSimilarEligibilities(queryText, limit = 10, minSimilarity = 0.7) {
    try {
      const res = await axios.post(
        `${BASE}/eligibility-embeddings/search`,
        { queryText, limit, minSimilarity },
        { withCredentials: true }
      );
      return res.data;
    } catch (error) {
      console.error('Error searching similar eligibilities:', error);
      throw error;
    }
  },

  async findEligibilitiesMatchingSummary(projectId, minSimilarity = 0.7, limit = 100) {
    try {
      const res = await axios.get(`${BASE}/eligibility-embeddings/match-summary`, {
        params: { projectId, minSimilarity, limit },
        withCredentials: true,
      });
      return res.data;
    } catch (error) {
      console.error('Error finding eligibilities matching summary:', error);
      throw error;
    }
  },

  // ═══════════════════════════════════════════════════════════════════
  // QUEUE-BASED SIMILARITY SEARCH (Background Jobs)
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Start a background similarity search job
   * @param {string} projectId - Project ID
   * @param {number} minSimilarity - Minimum similarity threshold (default: 0.65)
   * @param {number} limit - Maximum results (default: 100000)
   * @param {Array<string>} allowedNctIds - Optional: Only search within these NCT IDs (from filtered PostgreSQL data)
   */
  async startSimilaritySearch(projectId, minSimilarity = 0.65, limit = 100000, allowedNctIds = []) {
    try {
      const res = await axios.post(`${BASE}/similarity-search/start`, {
        projectId,
        minSimilarity,
        limit,
        allowedNctIds: Array.isArray(allowedNctIds) && allowedNctIds.length > 0 ? allowedNctIds : undefined
      }, {
        withCredentials: true
      });
      return res.data;
    } catch (error) {
      console.error('Error starting similarity search:', error);
      throw error;
    }
  },

  /**
   * Get status of a similarity search job
   */
  async getSimilaritySearchStatus(jobId) {
    try {
      const res = await axios.get(`${BASE}/similarity-search/status/${jobId}`, {
        withCredentials: true
      });
      return res.data;
    } catch (error) {
      console.error('Error getting job status:', error);
      throw error;
    }
  },

  /**
   * Get results of a completed similarity search job
   */
  async getSimilaritySearchResults(jobId) {
    try {
      const res = await axios.get(`${BASE}/similarity-search/results/${jobId}`, {
        withCredentials: true
      });
      return res.data;
    } catch (error) {
      console.error('Error getting job results:', error);
      throw error;
    }
  },

  /**
   * Cancel a queued similarity search job
   */
  async cancelSimilaritySearch(jobId) {
    try {
      const res = await axios.delete(`${BASE}/similarity-search/${jobId}`, {
        withCredentials: true
      });
      return res.data;
    } catch (error) {
      console.error('Error cancelling job:', error);
      throw error;
    }
  },

  /**
   * Helper: Poll for job completion
   */
  async pollSimilaritySearchJob(jobId, onProgress = null, intervalMs = 3000) {
    return new Promise((resolve, reject) => {
      const pollInterval = setInterval(async () => {
        try {
          const status = await this.getSimilaritySearchStatus(jobId);
          
          // Call progress callback if provided
          if (onProgress && typeof onProgress === 'function') {
            onProgress(status);
          }
          
          // Check if job is complete
          if (status.status === 'completed') {
            clearInterval(pollInterval);
            // Fetch results
            const results = await this.getSimilaritySearchResults(jobId);
            resolve(results);
          } else if (status.status === 'failed') {
            clearInterval(pollInterval);
            reject(new Error(status.error || 'Job failed'));
          }
          // Continue polling if queued or processing
          
        } catch (error) {
          clearInterval(pollInterval);
          reject(error);
        }
      }, intervalMs);
      
      // Set timeout (30 minutes)
      setTimeout(() => {
        clearInterval(pollInterval);
        reject(new Error('Job polling timeout after 30 minutes'));
      }, 30 * 60 * 1000);
    });
  },
};

export default feasibilityService;


